// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — SavedViewsMenu
//
// Reusable saved-views control for the Lead, Deal, and Task list toolbars. Lets a
// user save the current list filters as a named view, list/apply their own saved
// views, and delete a view — all through the additive `/api/v2/views` endpoints.
//
// When applying a view, the backend re-validates each stored filter and returns
// only the still-valid ones in `query`, along with any dropped filters in
// `skippedFilters`. This component forwards `query` to the caller (which applies
// it to the list's existing filter state) and surfaces a clear message listing
// which filters were dropped (Requirements 7.1, 7.2, 7.3, 7.5).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Bookmark, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Spinner } from '@/shared/components/ui/Spinner';
import { getApiErrorMessage } from '@/shared/api/axios';
import { cn } from '@/shared/utils/utils';
import { useSavedViews, useSavedViewMutations } from '../hooks/useSavedViews';
import type { SavedView, SkippedFilter, ViewEntityType, ViewFilters } from '../types';

export interface SavedViewsMenuProps {
  /** Which list entity these views belong to. */
  entityType: ViewEntityType;
  /**
   * The allowlisted filters currently active on the list, in the exact
   * query-param shape the backend expects (the page is responsible for building
   * this from its own filter state). Saved verbatim when the user saves a view.
   */
  currentFilters: ViewFilters;
  /**
   * Called with the still-valid, resolved filters after a view is applied. The
   * page merges these into its own filter state to re-run the list query.
   */
  onApply: (query: ViewFilters) => void;
}

/** Human-readable summary of dropped filters for the skipped-filter toast (Req 7.5). */
function describeSkipped(skipped: SkippedFilter[]): string {
  return skipped.map((s) => s.key).join(', ');
}

export function SavedViewsMenu({ entityType, currentFilters, onApply }: SavedViewsMenuProps) {
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<SavedView | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: views = [], isLoading } = useSavedViews(entityType);
  const { createView, applyView, deleteView } = useSavedViewMutations(entityType);

  // Close the popover on outside click (mirrors the shared Dropdown pattern).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleApply = (view: SavedView) => {
    applyView.mutate(view, {
      onSuccess: (res) => {
        onApply(res.query);
        setOpen(false);
        if (res.skippedFilters && res.skippedFilters.length > 0) {
          const label = describeSkipped(res.skippedFilters);
          const count = res.skippedFilters.length;
          toast(
            `Applied "${view.name}" — ${count} filter${count === 1 ? '' : 's'} skipped ` +
              `(no longer valid): ${label}`,
            { icon: '⚠️', duration: 6000 },
          );
        } else {
          toast.success(`Applied "${view.name}".`);
        }
      },
      onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to apply view.')),
    });
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Please enter a view name.');
      return;
    }
    createView.mutate(
      { name: trimmed, filters: currentFilters },
      {
        onSuccess: () => {
          toast.success(`Saved view "${trimmed}".`);
          setSaveOpen(false);
          setName('');
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to save view.')),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteView.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`Deleted view "${deleteTarget.name}".`);
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to delete view.')),
    });
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
        <Bookmark className="h-4 w-4" />
        Views
        {views.length > 0 && (
          <span className="ml-0.5 rounded-full bg-surface-variant px-1.5 text-body-sm text-on-surface-variant">
            {views.length}
          </span>
        )}
        <ChevronDown className="h-4 w-4" />
      </Button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-72 origin-top-right overflow-hidden rounded border border-outline bg-surface py-1 shadow-surface-3 animate-scale-in">
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner />
              </div>
            ) : views.length === 0 ? (
              <p className="px-3 py-4 text-center text-body-sm text-on-surface-variant">
                No saved views yet.
              </p>
            ) : (
              views.map((view) => (
                <div
                  key={view.id}
                  className="flex items-center gap-1 px-1.5 hover:bg-surface-variant"
                >
                  <button
                    type="button"
                    onClick={() => handleApply(view)}
                    disabled={applyView.isPending}
                    className={cn(
                      'flex-1 truncate rounded-card-sm px-2 py-2 text-left text-data-md text-on-surface',
                      'disabled:opacity-50',
                    )}
                    title={view.name}
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete view ${view.name}`}
                    onClick={() => setDeleteTarget(view)}
                    className="rounded-md p-1.5 text-on-surface-variant hover:bg-danger/10 hover:text-danger-text"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="mt-1 border-t border-outline pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSaveOpen(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-data-md text-on-surface transition-colors hover:bg-surface-variant"
            >
              <Plus className="h-4 w-4" />
              Save current view
            </button>
          </div>
        </div>
      )}

      {/* Save-view modal */}
      <Modal
        open={saveOpen}
        onClose={() => {
          setSaveOpen(false);
          setName('');
        }}
        title="Save current view"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setSaveOpen(false);
                setName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} loading={createView.isPending}>
              Save view
            </Button>
          </>
        }
      >
        <Input
          name="saved-view-name"
          label="View name"
          placeholder="e.g. Hot leads this month"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
          autoFocus
        />
        <p className="mt-2 text-body-sm text-on-surface-variant">
          Saves the filters currently applied to this list. You can apply it again anytime.
        </p>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete saved view"
        message={`Delete "${deleteTarget?.name ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteView.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
